import React, { useState, useEffect } from "react";
import { Navbar, Nav, Container, Button, NavDropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePageTitle } from '../context/PageTitleContext';
import { generateSlug } from '../utils/slugUtils';
import NotificationDropdown from './NotificationDropdown';
import "./Navigation.css";

function Navigation() {
  const { isAuthenticated, user, logout, token } = useAuth();
  const { pageTitle } = usePageTitle();
  const navigate = useNavigate();

  const [restaurantCategories, setRestaurantCategories] = useState([]);
  const [foodCategories, setFoodCategories] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [showCategoriesSubmenu, setShowCategoriesSubmenu] = useState(false);
  const [showCuisinesSubmenu, setShowCuisinesSubmenu] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Fetch restaurant categories
  useEffect(() => {
    fetch(`${apiBaseUrl}/restaurants/categories/`)
      .then((response) => response.json())
      .then((data) => {
        // Sort alphabetically by name for consistent display
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setRestaurantCategories(sortedData);
      })
      .catch((error) => console.error('Error fetching restaurant categories:', error));
  }, [apiBaseUrl]);

  // Fetch food categories
  useEffect(() => {
    fetch(`${apiBaseUrl}/foods/categories/`)
      .then((response) => response.json())
      .then((data) => {
        // Sort alphabetically by name for consistent display
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setFoodCategories(sortedData);
      })
      .catch((error) => console.error('Error fetching food categories:', error));
  }, [apiBaseUrl]);

  // Fetch cuisines
  useEffect(() => {
    fetch(`${apiBaseUrl}/cuisines/`)
      .then((response) => response.json())
      .then((data) => setCuisines(data))
      .catch((error) => console.error('Error fetching cuisines:', error));
  }, [apiBaseUrl]);

  // Fetch unread notifications count
  const fetchUnreadCount = async () => {
    if (!isAuthenticated || !token) return;

    try {
      const response = await fetch(`${apiBaseUrl}/users/social/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      } else if (response.status === 401) {
        // Token expired or invalid - stop polling
        console.log('Authentication failed for notifications, stopping polling');
        return;
      }
    } catch (error) {
      // Only log if not a network error during logout
      if (isAuthenticated) {
        console.error('Error fetching unread notifications count:', error);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, token, apiBaseUrl]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setExpanded(false); // Close menu after logout
  };

  const closeMenu = () => {
    setExpanded(false);
  };

  return (
    <Navbar bg="white" expand="lg" className="shadow-sm sticky-top" expanded={expanded} onToggle={setExpanded}>
      <Container>
        <Navbar.Brand as={Link} to="/about" className="brand-logo d-flex align-items-center" onClick={closeMenu}>
          <img
            src="/wide-logo.jpeg"
            alt="dyner.z"
            height="40"
            className="d-inline-block align-top me-2"
          />
        </Navbar.Brand>

        {/* Page Title - shows in center */}
        {pageTitle && (
          <div className="page-title-navbar">
            <strong>{pageTitle}</strong>
          </div>
        )}

        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto">
            <NavDropdown title="Restaurants" id="restaurants-dropdown">
              <NavDropdown.Item as={Link} to="/restaurants" onClick={closeMenu}>
                All Restaurants
              </NavDropdown.Item>
              {restaurantCategories.length > 0 && <NavDropdown.Divider />}
              {restaurantCategories.map((category) => (
                <NavDropdown.Item
                  key={category.id}
                  as={Link}
                  to={`/restaurant-category/${category.id}/${generateSlug(category.name)}`}
                  onClick={closeMenu}
                >
                  {category.name}
                </NavDropdown.Item>
              ))}
              {isAuthenticated && (
                <>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/add-restaurant" className="text-primary fw-bold" onClick={closeMenu}>
                    + Add Restaurant
                  </NavDropdown.Item>
                </>
              )}
            </NavDropdown>

            <NavDropdown title="Foods" id="foods-dropdown">
              <NavDropdown.Item as={Link} to="/foods" onClick={closeMenu}>
                All Foods
              </NavDropdown.Item>

              {foodCategories.length > 0 && (
                <>
                  <NavDropdown.Divider />
                  <div className={`dropdown-submenu ${showCategoriesSubmenu ? 'show' : ''}`}>
                    <div
                      className="dropdown-item dropdown-toggle"
                      onClick={() => setShowCategoriesSubmenu(!showCategoriesSubmenu)}
                    >
                      Categories
                    </div>
                    <div className="dropdown-menu">
                      {foodCategories.map((category) => (
                        <NavDropdown.Item
                          key={category.id}
                          as={Link}
                          to={`/food-category/${category.id}/${generateSlug(category.name)}`}
                          onClick={closeMenu}
                        >
                          {category.name}
                        </NavDropdown.Item>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {cuisines.length > 0 && (
                <>
                  <div className={`dropdown-submenu ${showCuisinesSubmenu ? 'show' : ''}`}>
                    <div
                      className="dropdown-item dropdown-toggle"
                      onClick={() => setShowCuisinesSubmenu(!showCuisinesSubmenu)}
                    >
                      Cuisines
                    </div>
                    <div className="dropdown-menu">
                      {cuisines.map((cuisine) => (
                        <NavDropdown.Item
                          key={cuisine.id}
                          as={Link}
                          to={`/cuisine/${cuisine.id}/${generateSlug(cuisine.name)}/foods`}
                          onClick={closeMenu}
                        >
                          {cuisine.name}
                        </NavDropdown.Item>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </NavDropdown>

            {isAuthenticated ? (
              <>
                <Nav.Item className="d-flex align-items-center">
                  <NotificationDropdown
                    token={token}
                    unreadCount={unreadCount}
                    onNotificationUpdate={fetchUnreadCount}
                  />
                </Nav.Item>
                <Nav.Link as={Link} to="/profile" onClick={closeMenu}>
                  {user?.first_name || user?.username || user?.email}
                </Nav.Link>
                <Button variant="outline-primary" size="sm" onClick={handleLogout} className="ms-2">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/saved" onClick={closeMenu}>
                  Saved
                </Nav.Link>
                <Nav.Link as={Link} to="/login" onClick={closeMenu}>
                  Login
                </Nav.Link>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default Navigation;
